import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import moment from 'moment';

import { BASENAME, CHARGE_KIND, CHARGE_KIND_DISPLAY, LAYOUT_TYPES, UNEXPECTED_ERROR_MESSAGE } from '../../../constants';
import LineTitleGray from '../../atoms/titles/LineTitleGray';
import LineTitle from '../../atoms/titles/LineTitle';
import UserInfoTable from '../../organisms/UserInfoTable';
import SubscriptionNoteTable from '../../molecules/SubscriptionNoteTable';
import RadioCash from '../../atoms/form/RadioCash';
import RadioCampaign from '../../atoms/form/RadioCampaign';
import Textlink from '../../atoms/link/Textlink';
import LinkButton from '../../atoms/button/LinkButton';
import CheckBox from '../../atoms/form/CheckBox';
import RadioGift from '../../atoms/form/RadioGift';
import RadioWesterPoint from '../../atoms/form/RadioWesterPoint';
import GiftCardInput from '../../molecules/GiftCardInput';
import UserInfoPlanContents from '../../organisms/UserInfoPlanContents';
import { setCurrentStep, setLayoutType } from '../../../modules/app';
import environments  from '../../../environments';
import { RELATIONSHIP_OPTIONS, COUPON_TYPES, ENABLE_GIFT_CARD_FEATURE }  from '../../../constants';
import { getPlanFromSessionPlansByDate } from '../../../utils/plans';
import { getPlanOptionFromPlanOptionsByDate } from '../../../utils/planOptions';
import { getPlanOptionsFromSessionLands } from '../../../utils/planOptions';
import CustomerInfoDisplay from '../../molecules/CustomerInfoDisplay';
import { FetchCoupon } from '../../../queries/index.graphql';
import { makeSessionCoupon, makeSessionAgreeMinorOnlyFlag, makeSessionAgreeConsiderationFlag, makeSessionWester } from '../../../utils/input';
import { fetchSession, updateSession, useGiftCards, useTimeout } from '../../../modules/session';
import Loading from '../../atoms/Loading';
import { getCouponInfoFromCookie } from '../../../utils/cookie';
import { FDataFromSession } from '../../Head';
import ModalCampaignCaption from '../../modal/ModalCampaignCaption';
import ModalGiftCardCaption from '../../modal/ModalGiftCardCaption';
import ModalConfirmFreeReservation from '../../modal/ModalConfrimFreeReservation';
import FinalPrice from '../../molecules/FinalPrice';
import TimeoutErrorModal from '../../atoms/modal/TimeoutErrorModal';
import { getIsAna2 } from '../../../utils/Ana2';

const FinalConfirmation = ({ session: initialSession }) => {
  const dispatch = useDispatch();
  const currentStep = 4;

  const [session, setSession] = useState(initialSession);
  const [coupon, setCoupon] = useState(initialSession.coupon);
  const [haveChildren, setHaveChildren] = useState(false);
  const [minorOnlyFlag, setMinorOnlyFlag] = useState(false);
  const [considerationFlag, setConsiderationFlag] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [showTripCondition, setShowTripCondition] = useState(true);
  const [openCampaignModal, setOpenCampaignModal] = useState(false);
  const [openGiftCardModal, setOpenGiftCardModal] = useState(false);
  const [openConfirmFreeModal, setOpenConfirmFreeModal] = useState(false);
  const giftCardEnterForm = useRef(null);
  const reserveForm = useRef(null);
  const enabledWesterPointInitial = (initialSession.wester && initialSession.wester.usePoint != null);
  const [enabledWesterPoint, setEnabledWesterPoint] = useState(enabledWesterPointInitial);
  const [westerUsePoint, setWesterUsePoint] = useState(enabledWesterPointInitial ? initialSession.wester.usePoint : 0);
  const [westerPointValidationError, setWesterPointValidationError] = useState('');

  useEffect(() => {
    dispatch(
      setLayoutType(LAYOUT_TYPES.step)
    );
    dispatch(setCurrentStep(currentStep));

    return () => {
      dispatch(
        setLayoutType(LAYOUT_TYPES.default)
      );
    };
  }, []);

  const { isTimeoutError } = useTimeout();

  const { siteId, discountCode, transactionId } = getCouponInfoFromCookie();
  const { data, loading, error } = useQuery(
    FetchCoupon,
    {
      variables: { sessionID: initialSession.id, siteID: siteId, discountCode, transactionId },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: (error) => {
        if (error.graphQLErrors && error.graphQLErrors.length) {
          const firstError = error.graphQLErrors[0];
          const errorCode = firstError.debugMessage || firstError.message;
          if(errorCode === '999'){
            window.open(`${BASENAME}/search?error=999&errorMsg=${UNEXPECTED_ERROR_MESSAGE}`, '_self');
          }
        }
        if (error.networkError) {
          console.error('Network Error:', error.networkError);
        }
      },
    },
  );

  if (!isTimeoutError && error && error.graphQLErrors && error.graphQLErrors.length) {
    const firstError = error.graphQLErrors[0];
    const redirectUrl = firstError.extensions && firstError.extensions.redirect;
    window.open(redirectUrl, '_self');
  }

  const coupons = (data && data.coupon) || [];

  // https://gitlab.fqsv.net/dp-plus/front/issues/165#note_29470
  const refetchSession = async () => {
    if (isTimeoutError) {
      return;
    }

    try {
      const session = await fetchSession();
      setSession(session);

    } catch (error) {
      setFetching(false);
      let errorCode = [];
      if (error && error.graphQLErrors && error.graphQLErrors.length) {
        errorCode = error.graphQLErrors.map((e) => (e.debugMessage || e.message)).filter(Boolean);
      }

      if(errorCode[0] === '999'){
        window.open(`${BASENAME}/search?error=999&errorMsg=${UNEXPECTED_ERROR_MESSAGE}`, '_self');
      }
    }

  };

  useEffect(() => {
    if (!loading) {
      refetchSession();
    }
  }, [loading]);

  const {
    id: sessionId, flightSchedule, plans, member, accompanies,
    office, people, giftCard: initialGiftCardInfo, lands,
  } = session;

  const {
    enabledGiftCard, onChangeEnabledGiftCard, giftCards, giftCardInputTotalNumber, isEnteredByUser,
    onChangeGiftCardInputAmount, onClickGiftLink, giftCardEnterUrl, clearGiftCardInputAmount,
  } = useGiftCards(dispatch, initialGiftCardInfo, giftCardEnterForm);

  const handleCouponChange = (coupon) => {
    setCoupon(coupon);

    const sessionCoupon = makeSessionCoupon(coupon);
    dispatch(updateSession(sessionCoupon));

    const isEnteredGiftAmonut = isEnteredByUser && giftCardInputTotalNumber > 0;
    if (isEnteredGiftAmonut) {
      clearGiftCardInputAmount();
      alert('請求金額が変更されました。ギフトカード利用金額を入力しなおしてください。');
    }
  };

  const infantNum = session.people.infant || 0;
  const childrenCNum = session.people.childrenC || 0;
  const children2Num = session.people.children2 || 0;
  const haveAnyInfant = infantNum > 0 || childrenCNum > 0 || children2Num > 0;

  const handleBack = () => {
    window.open(`${BASENAME}/entry_customer`, '_self');
  };

  const actionPageUrl = `${environments.API_BASE_URI}/reserve`;

  const outboundFlight = flightSchedule.departure;
  const inboundFlight = flightSchedule.arrival;

  const discount = coupon ? coupon.totalDiscount || 0 : 0;

  const totalPrice = session.totalAmount;

  const finalPriceHalf = (totalPrice - discount) * 0.5; //westerポイントの上限値
  const finalPrice = totalPrice - discount - (westerUsePoint || 0);

  useEffect(() => {
    const _validCoupons = filterValidCoupons(coupons, totalPrice);
    if (hasDiscount(_validCoupons)) {
      const discount = _validCoupons.find((c) => c && c.type === COUPON_TYPES.discount);
      handleCouponChange(discount);
    }
  }, [coupons]);

  const validCoupons = filterValidCoupons(coupons, totalPrice);

  const tripStartDayString = outboundFlight.departureTime.substr(0, 10);
  const tripStartDate = moment(tripStartDayString);
  const tripEndDayString = inboundFlight.arrivalTime.substr(0, 10);
  const tripEndDate = moment(tripEndDayString);
  const tripTotalDays = tripEndDate.diff(tripStartDate, 'days') + 1;
  const planOptions = getPlanOptionsFromSessionLands(lands);

  const schedule = new Array(tripTotalDays).fill(undefined).map((_, i) => {
    const date = tripStartDate.clone().add(i, 'days');

    const plan = getPlanFromSessionPlansByDate(plans, date);
    const planOptionsByDate = getPlanOptionFromPlanOptionsByDate(planOptions, date);

    return {
      date,
      plan,
      planOptions: planOptionsByDate,
    };
  });

  const todayDisplay =  moment().format('YYYY年M月D日(ddd)');

  const { name, note, lifeSupportNumber, phoneticName } = member;

  const tripConditionUrl = 'http://rsv.nta.co.jp/F_Booking/static/ryokojoken_shusai.htm';
  const giftCardTermLink = 'https://www.nta.co.jp/nrs/images/gift/gift.pdf';
  const dreamCardTermLink = 'https://www.nta.co.jp/nrs/dreamplan/dreamplan.pdf';

  const termsConditionLink = `${environments.API_BASE_URI}/terms-condition?s=${sessionId}`;
  const onClickTermsCondition = () => window.open(termsConditionLink, '_blank');

  const isCommunitySupport = coupon && coupon.goto && coupon.goto.communitySupport;

  const validGiftCardInputTotalNumber = enabledGiftCard ? giftCardInputTotalNumber : 0;
  const billingPrice = finalPrice - validGiftCardInputTotalNumber;
  const billingPriceWithoutWesterPoint = finalPrice + (westerUsePoint || 0) - validGiftCardInputTotalNumber;

  const isWesterMember = session.wester ? true : false;
  const westerPointLimit = session.wester ? (session.wester.pointLimit ? session.wester.pointLimit : 0) : 0;

  useEffect(() => {
    if (!enabledWesterPoint) {
      setWesterPointValidationError('');
      return;
    }
    const errorMessage = validateWesterPointErrorMessage(westerUsePoint, finalPriceHalf, westerPointLimit, billingPriceWithoutWesterPoint, isWesterMember, enabledWesterPoint);
    setWesterPointValidationError(errorMessage);
  }, [enabledWesterPoint, westerUsePoint, finalPrice, westerPointLimit, billingPrice]);


  const makePaymentMethodDisplay = (giftCardInputTotalNumber) => {
    let paymentMethodDisplay = 'クレジットカード';
    if (!(enabledGiftCard && giftCardInputTotalNumber > 0)) {
      return paymentMethodDisplay;
    }

    if (finalPrice == giftCardInputTotalNumber) {
      paymentMethodDisplay = '日本旅行ギフトカード・ドリーム旅行カード';
      return paymentMethodDisplay;
    }

    paymentMethodDisplay = '日本旅行ギフトカード・ドリーム旅行カード（クレジットカード併用可能）';
    return paymentMethodDisplay;
  };

  const isFullPaidWithGiftCard = enabledGiftCard && finalPrice == giftCardInputTotalNumber;

  const makeNextPageLabel = () => {
    if (isFullPaidWithGiftCard) {
      return '予約を完了する';
    }

    return 'クレジットカード情報入力へ進む';
  };

  const confirmReservationWithGiftCard = () => {
    setOpenConfirmFreeModal(true);
  };

  const cancelReservationWithGiftCard = () => {
    setOpenConfirmFreeModal(false);
  };

  const handleSelectHaveChildren = (value) => {
    setHaveChildren(value);
    if(value === false){
      setMinorOnlyFlag(false);
      dispatch(updateSession(makeSessionAgreeMinorOnlyFlag(false)));
    }
  };

  const handleSelectMinorOnlyFlag = () => {
    const newValue = !minorOnlyFlag;
    setMinorOnlyFlag(newValue);
    dispatch(updateSession(makeSessionAgreeMinorOnlyFlag(newValue)));
  };

  const handleSelectConsiderationFlag = (value) => {
    setConsiderationFlag(value);
    dispatch(updateSession(makeSessionAgreeConsiderationFlag(value)));
  };

  const doReserve = () => {
    reserveForm.current && reserveForm.current.submit();
  };

  const handleClickReserve = () => {
    if (isFullPaidWithGiftCard) {
      confirmReservationWithGiftCard();
      return;
    }

    doReserve();
  };

  const handleWesterPointChange = (changedWesterPoint) => {

    // ポイント数のバリデーション
    const westerPointErrorMessage = validateWesterPointErrorMessage(changedWesterPoint.usePoint, finalPriceHalf, westerPointLimit, billingPriceWithoutWesterPoint, isWesterMember, changedWesterPoint.enabledWesterPoint);
    if (westerPointErrorMessage) {
      setWesterPointValidationError(westerPointErrorMessage);
      return;
    }
    // 値が「元に戻った」などで state が変わらないケースでも、表示エラーは確実に消す
    setWesterPointValidationError('');

    setEnabledWesterPoint(changedWesterPoint.enabledWesterPoint);
    setWesterUsePoint(changedWesterPoint.usePoint);

    const sessionWester = makeSessionWester(changedWesterPoint); // { enabledWesterPoint: true, usePoint: 10000 }
    dispatch(updateSession(sessionWester));

    const isEnteredGiftAmonut = isEnteredByUser && giftCardInputTotalNumber > 0;
    if (isEnteredGiftAmonut) {
      clearGiftCardInputAmount();
      alert('請求金額が変更されました。ギフトカード利用金額を入力しなおしてください。');
    }
  };

  const validateWesterPointErrorMessage = (usePoint, finalPriceHalf, westerPointLimit, billingPriceWithoutWesterPoint, isWesterMember, enabledWesterPoint) => {
    const formatErrMsg = '【入力エラー】ポイント数を半角数字で入力ください。';
    const limitErrMsg = '【入力エラー】ご利用可能なポイント数の上限を超えています。';
    if (!isWesterMember || !enabledWesterPoint) {
      return '';
    }
    if (!Number.isFinite(usePoint) || !Number.isInteger(usePoint)) {
      return formatErrMsg;
    }
    if (usePoint < 0) {
      return formatErrMsg;
    }
    if (usePoint > westerPointLimit) {
      return limitErrMsg;
    }
    if (usePoint > finalPriceHalf) {
      return limitErrMsg;
    }
    if (usePoint > billingPriceWithoutWesterPoint) {
      return limitErrMsg;
    }
    return '';
  };

  const SelectedOutboundFlight = flightSchedule.departure;
  const SelectedOutboundDate = SelectedOutboundFlight.departureTime.substr(0, 10);
  const isAna2 = getIsAna2(SelectedOutboundFlight.airline, SelectedOutboundDate);

  return (
    <div className="pageEntryConfirmation">
      <FDataFromSession session={session} step={currentStep} />
      <Loading show={!isTimeoutError && loading} hideProgressBar progressRate={100} />
      <div className="EntryConfirmationBlock">
        <div className="EntryConfirmationBlockTitle">
          <LineTitleGray>申込み内容の最終確認</LineTitleGray>
        </div>
        <div className="EntryConfirmationBlockTable">
          <UserInfoTable
            tripTotalDays={tripTotalDays}
            schedule={schedule}
            outboundSessionFlight={outboundFlight}
            inboundSessionFlight={inboundFlight}
          />
        </div>
        <div className="EntryConfirmationPlanContents">
          <UserInfoPlanContents
            tripTotalDays={tripTotalDays}
            schedule={schedule}
            outboundSessionFlight={outboundFlight}
            inboundSessionFlight={inboundFlight}
          />
        </div>
      </div>

      <div className="EntryConfirmationBlock">
        <LineTitleGray>お客様情報確認</LineTitleGray>
        <div className="EntryConfirmationItem">
          <LineTitle text="代表者情報" />
          <CustomerInfoDisplay
            customer={member}
            representative
            isAna2={isAna2}
            planOptions={planOptions}
            customerIndex={0}
          />
        </div>
        <div className="EntryConfirmationItem">
          <LineTitle text="同行者情報" />
          {accompanies.map((a, index) => {
            const { chargeKind } = a;
            const titleIndex = index + 1;
            const titleLabel = `同行者${titleIndex} ${CHARGE_KIND_DISPLAY[chargeKind]}`;

            return (
              <React.Fragment key={index}>
                <div className="wrapperLabel">{titleLabel}</div>
                <CustomerInfoDisplay
                  customer={a}
                  isAna2={isAna2}
                  planOptions={planOptions}
                  customerIndex={titleIndex}
                />
              </React.Fragment>
            );
          })}
        </div>
        <div className="EntryConfirmationItem">
          <LineTitle text="部屋割り" />
          <div className="userInfoItem">
            <div className="userInfoItemName">代表者</div>
            <div className="userInfoItemName phoneticName">{phoneticName.last} {phoneticName.first}</div>
            <div className="userInfoItemName">1部屋目</div>
          </div>
          {accompanies.map((a, index) => {
            const { name, chargeKind, roomAllocation } = a;
            let titleIndex = index + 1;
            const titleLabel = `同行者${titleIndex}`;

            if (chargeKind === CHARGE_KIND.infant || chargeKind === CHARGE_KIND.childrenC || chargeKind === CHARGE_KIND.children2) {
              return null;
            }

            if (chargeKind === CHARGE_KIND.childrenA) {
              titleIndex -= people.adult - 1;
            } else if (chargeKind === CHARGE_KIND.childrenB) {
              titleIndex -= (people.adult - 1 + people.childrenA || 0);
            }

            return (
              <div className="userInfoItem" key={index}>
                <div className="userInfoItemName">{titleLabel}</div>
                <div className="userInfoItemName phoneticName">{name.last} {name.first}</div>
                <div className="userInfoItemName">{roomAllocation}部屋目</div>
              </div>
            );
          })}
          {haveAnyInfant && (
            <div className="userInfoItem noteTextRed" >※こども(0～5歳)の寝具なし（添い寝）のお子様は部屋割りには含まれません。</div>
          )}
        </div>
        {lifeSupportNumber && (
          <div className="EntryConfirmationItem">
            <LineTitle text="ライフサポート倶楽部会員番号" />
            <div className="userInfoItem">{lifeSupportNumber}</div>
          </div>
        )}
      </div>
      {isWesterMember && (
        <div className="EntryConfirmationBlock">
          <LineTitleGray>WESTERポイント</LineTitleGray>
          <div className="userInfoWesterPoint">
            <div className="userInfoWesterPointTitle">利用可能なWESTERポイント</div>
            <div className="userInfoWesterLimitPoint">{westerPointLimit.toLocaleString()}ポイント</div>
            <RadioWesterPoint
              enabledWesterPoint={enabledWesterPoint}
              usePoint={westerUsePoint ? westerUsePoint : 0}
              westerPointValidationError={westerPointValidationError}
              onChange={handleWesterPointChange}
            />
            <div className="userInfoWesterPointNote">
              <p className="userInfoWesterPointNoteTitle">【利用WESTERポイントについて】</p>
              <p>※1ポイント＝1円としてご利用いただけます。</p>
              <p>※最大利用可能ポイント数はご旅行代金の50%までご利用いただけます。（ご利用単位：1ポイント）</p>
              <p>※利用WESTERポイントについて、申込み後の変更はできませんのでご了承ください。</p>
            </div>
            <div className="userInfoWesterPointLink">
              <a href="https://wester.jr-odekake.net/service/ntaweb/" target="_blank" rel="noopener noreferrer">WESTERポイント付与に関するご案内　〉</a>
            </div>
          </div>
        </div>
      )}
      <div className="EntryConfirmationBlock">
        <LineTitleGray>お支払い方法選択</LineTitleGray>
        <div className="userInfoCash">
          <div className="">
            <div className="userInfoCashItemCampaignTitle">
              お客様がご利用可能な割引・キャンペーン
              <div
                className="iconWrapper"
                onClick={() => setOpenCampaignModal(true)}
              >
                <img
                  className="questionIcon"
                  src="/dp-air/img/icons/question.png"
                />
              </div>
            </div>
            <ModalCampaignCaption open={openCampaignModal} onClose={() => setOpenCampaignModal(false)}/>
            <div className="userInfoCashItemCampaignRadio">
              <RadioCampaign
                coupons={validCoupons}
                onChange={handleCouponChange}
                selectedCoupon={coupon}
                hideNoSelected={hasDiscount(validCoupons)}
              />
            </div>
          </div>
          {ENABLE_GIFT_CARD_FEATURE && (
            <React.Fragment>
              <div className="userInfoCashItemCampaign">
                <div className="userInfoCashItemCampaignTitle">
                  日本旅行ギフトカード・ドリーム旅行カード
                  <div
                    className="iconWrapper"
                    onClick={() => setOpenGiftCardModal(true)}
                  >
                    <img
                      className="questionIcon"
                      src="/dp-air/img/icons/question.png"
                    />
                  </div>
                </div>
                <ModalGiftCardCaption open={openGiftCardModal} onClose={() => setOpenGiftCardModal(false)}/>
                <div className="userInfoCashItemCampaignRadio">
                  <RadioGift
                    enabledGiftCard={enabledGiftCard}
                    onChangeEnabledGiftCard={onChangeEnabledGiftCard}
                  />
                </div>
                {enabledGiftCard && (
                  <React.Fragment>
                    <div className="userInfoCashItemGiftDesc">
                      <Textlink to={giftCardTermLink} outgoing>ギフトカード[PDF形式]</Textlink>&nbsp;
                      <Textlink to={dreamCardTermLink} outgoing>ドリーム旅行カード[PDF形式]</Textlink>&nbsp;のご利用についてご一読・同意の上、「カード番号を入力する」ボタンを押してください。
                    </div>
                    <div className="userInfoCashItemGiftDesc">
                      <LinkButton onClick={onClickGiftLink}>
                        <div className="userInfoCashItemGiftInputLink">
                          <img src="/dp-air/img/icons/icon_checked.png" alt="" style={{ height: 20, width: 20 }} />
                          同意の上、カード番号を入力する<br />
                          <span className="userInfoCashItemGiftInputLinkNote">（日本旅行ギフトカード・ドリーム旅行カード）</span>
                        </div>
                      </LinkButton>
                    </div>
                    <form action={giftCardEnterUrl} method="POST" ref={giftCardEnterForm} >
                      <input hidden name="s" value={sessionId} readOnly />
                    </form>
                  </React.Fragment>
                )}
              </div>
              {enabledGiftCard && (
                <div className="giftCardList">
                  {giftCards && giftCards.map((g, i) => (
                    <GiftCardInput
                      key={g.giftCardKey}
                      number={i+1}
                      availableAmount={g.balance}
                      inputAmount={g.inputAmount}
                      onChangeInputAmount={(input) => onChangeGiftCardInputAmount(g.giftCardKey, input)}
                      giftCardNumber={g.giftCardNumber}
                      expirationDate={g.expirationDate}
                      finalPriceNumber={finalPrice}
                      giftCardInputTotalNumber={giftCardInputTotalNumber}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          )}
          <div className="userInfoCashItem userInfoCashItemCampaign">
            <div className="userInfoCashItemTitle">お支払い方法</div>
            <div className="userInfoCashItemRadio">
              <RadioCash optionName={makePaymentMethodDisplay(giftCardInputTotalNumber)} />
            </div>
          </div>
        </div>
      </div>
      <FinalPrice
        className="finalPrice"
        totalPrice={totalPrice}
        discount={discount}
        finalPrice={finalPrice}
        giftCardInputTotal={giftCardInputTotalNumber}
        showGiftCardInputTotal={enabledGiftCard}
        billingPrice={billingPrice}
        member={member}
        accompanies={accompanies}
        westerUsePoint={westerUsePoint}
      />
      {!!(coupon && coupon.goto && coupon.goto.regionalCoupon) && (
        <div className="EntryConfirmationBlock">
          <LineTitleGray>
            {isCommunitySupport ? 'この予約で付与される地域クーポン' : 'この予約で付与される地域クーポン'}
          </LineTitleGray>
          <div className="EntryConfirmationItem">
            <div className="regionalCoupon">
              <div className="regionalCouponTitle">地域クーポン</div>
              <div className="regionalCouponValue">
                {`${(coupon.goto.regionalCoupon || 0).toLocaleString()}円分`}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="EntryConfirmationBlock">
        <LineTitleGray>申し込み注意事項</LineTitleGray>
        <div className="userInfoNote">
          <LineTitle text="取消規定" />
          <div className="noteItemBody">
            <p className="noteText noteTextStrong">
            取消料発生日：
              <span className="noteTextRed">{todayDisplay}</span>から取り消し料がかかります。
            </p>
            <div className="noteTable noteTablePC">
              <SubscriptionNoteTable />
            </div>
            <div className="noteTable noteTableSP">
              <SubscriptionNoteTable />
            </div>
            <div className="noteText">
              取消に関するご注意<br />
              【ご利用に関するご案内】<br />
              予約を取り消された場合は、ご予約取扱店舗で、旅行代金に対して、上記の率による取消料をいただき残額を払い戻します。<br />
              お客様のご都合で出発日、コース、宿泊ホテル等を変更される場合も取り消しの上再予約となるため、旅行代金全額に対して上記の取消料が適用されます。<br />
              なお、お客様がJR西日本が提供する「WESTERポイント」をご利用してご予約された場合、いかなる理由であっても「WESTERポイント」は取消料に充当いたしません。<br />
              「旅行契約の解除期日」とはお客様が当社らのそれぞれの営業日・営業時間内に解除する旨をお申し出いただいた時を基準とします。<br />
              ※旅行代金全額とは「WESTERポイント」利用分を差引く前の旅行代金を指します。<br />
              取消手続き完了後、利用されたポイントは返却されます（返却まで5営業日程度かかる場合があります）。<br />
              ただし、WESTERポイント（用途・期間限定）をご利用の場合、取消時に有効期限切れとなっている場合は返却されません。
            </div>
            <div className="noteText">
              <div className="mb-5">予約完了後、お客様のご都合で予約取り消しを行った場合、即時で取消料一覧に定めた取消料をいただきます。</div>
              詳しい条件などは<Textlink to={tripConditionUrl} outgoing>こちら</Textlink>をご確認ください。
            </div>
          </div>
        </div>
        <div className="EntryConfirmationItem">
          <div className="noteItemTable">
            <div className="noteItemBody">
              <div className="noteTextStrong">ご予約後の変更について</div>
              <div>現予約を取消後、新規にご予約のお取り直しをお願いいたします。取消には所定の取消料が必要です。お申込み期限を切った場合は再予約もできません。また、旅行者氏名・年齢の訂正もできません。新たにご予約のお取り直しをお願いいたします。</div>
            </div>
            <div className="noteItemBody">
              <div className="noteTextRed noteText noteTextStrong">お子様（こども）について</div>
              <div className="noteTextRed">
                6歳以上のお子様は航空座席のみ（添い寝）のご予約はお取扱はできません。<br />
                こども(0～5歳)のお子様は「航空座席・寝具・食事なし」の予約が可能となります。<br />
                ※ただし、2026/05/19以降のANA便利用はこども(2歳)のお子様は「航空座席あり、寝具・食事なし」となります。
              </div>
            </div>
          </div>
        </div>
        <div className="userInfoNote">
          <LineTitle text="取引条件説明書" />
          <div className="conditionItemBody">
            <div className="conditionItemBodyText">
              <div className="conditionItemBodyTextMain">お申込みの際は必ず<span className="bold">取引条件書面（旅行条件書）</span>をお読みください。</div>
            </div>
            <div className="conditionItemBodyButton">
              <LinkButton onClick={onClickTermsCondition}>取引条件説明書を表示</LinkButton>
            </div>
          </div>
        </div>
        <div className="userInfoNote">
          <LineTitle text="未成年の方のお申込みについて" />
          <p className="conditionItemText">未成年者の旅行契約には、親権者の同意が必要です。</p>
          <div className="EntryConfirmationItem agreeMinorOnlyFlag">
            <div className="radio radioHaveChildren">
              <div className="radioItem">
                <input type="radio" name="haveChildren" id="haveChildren1" className="radio-input" 
                checked={haveChildren === false} onChange={() => handleSelectHaveChildren(false)} />
                <label htmlFor="haveChildren1">未成年参加者がいない</label>
              </div>
              <div className="radioItem">
                <input type="radio" name="haveChildren" id="haveChildren2" className="radio-input" 
                checked={haveChildren === true} onChange={() => handleSelectHaveChildren(true)} />
                <label htmlFor="haveChildren2">未成年参加者がいる</label>
              </div>
            </div>
            {haveChildren && (
              <div className="entryCheck">
                <div>
                  未成年者の旅行契約には、親権者の同意が必要です。<br />
                  未成年者の方のみでのご参加、又は親権者以外の方（成人を含む）が同行される場合には、親権者の同意が必要です。<br />
                  また、15歳未満の未成年者がご旅行に参加される場合には、原則として親権者の方のご同行が必要となります。<br />
                  ※未成年者の参加者全員が事前に親権者から同意を得た上でお申込みください。
                </div>
                <div className="minorOnlyFlagCheckBoxWrap">
                  <CheckBox name="minorOnlyFlag" id="minorOnlyFlag" bold={true} checked={minorOnlyFlag} onChange={() => handleSelectMinorOnlyFlag()}>
                    未成年者の参加者全員が親権者からの同意を得ています
                  </CheckBox>
                </div>
              </div>
            )} 
          </div>
        </div>
        <div className="userInfoNote">
          <LineTitle text="特別な配慮を必要とするの方のお申込みについて" />
          <div className="EntryConfirmationItem agreeConsiderationFlag">
            <div className="radio radioHaveChildren">
              <div className="radioItem">
                <input type="radio" name="ConsiderationFlag" id="ConsiderationFlag1" className="radio-input" 
                checked={considerationFlag === false} onChange={() => handleSelectConsiderationFlag(false)} />
                <label htmlFor="ConsiderationFlag1">特別な配慮を必要とする方がいない</label>
              </div>
              <div className="radioItem">
                <input type="radio" name="ConsiderationFlag" id="ConsiderationFlag2" className="radio-input" 
                checked={considerationFlag === true} onChange={() => handleSelectConsiderationFlag(true)} />
                <label htmlFor="ConsiderationFlag2">特別な配慮を必要とする方がいる</label>
              </div>
            </div>
            {considerationFlag && (
              <div>
                お客様の状況により、当初の手配内容に含まれていない特別な配慮、措置が必要な方はマイページのお問い合わせ一覧より取扱店舗にお申し出ください。<br />
                ※なお、食事等のアレルギーについてのご相談がある場合は、宿泊施設へ直接お申し出ください。
              </div>
            )} 
          </div>
        </div>
        <div className="userInfoNote">
          <div className="entryCheck">
            <div>
              ご予約後に申込内容の変更はできません。今一度、本ページ記載の申込内容についてご確認ください。<br />
              下記の内容をご確認・ご同意いただきご予約ください。<br/>
              ・申込内容に相違がないこと<br />
              ・旅行行程や時間を確認し、相違がないこと<br />
              ・取引条件説明書面を電磁的方法で交付を受けること<br />
              ・取引条件説明書面の記載内容を必ずご確認いただき、取引条件説明を受けたものとして取扱うこと<br />
              ・旅行条件書に定める個人情報の取扱について<br />
              ・「未成年の方のお申込みについて」及び「特別な配慮を必要とする方のお申込みについて」のご申告に相違がないこと
            </div>
            <div className="entryCheckBoxWrap">
              <CheckBox name="entryCheck" id="entryCheck" checked={agreed} onChange={() => setAgreed(!agreed)} disabled={isTimeoutError} bold={true}>
                  上記に同意の上、申込みます
              </CheckBox>
            </div>
          </div>
          <div className="subscriptionCheckActions">
            <div className="linkActions">
              <div className="linkClose">
                <LinkButton buttonType="navigate" onClick={handleBack}>戻る</LinkButton>
              </div>
              {!isTimeoutError && (
                <div className="linkMypage">
                  <LinkButton buttonType="cta" disabled={(!agreed || (haveChildren && !minorOnlyFlag) || (westerPointValidationError !== ''))} onClick={handleClickReserve}>{makeNextPageLabel()}</LinkButton>
                  <form ref={reserveForm} action={actionPageUrl} method="POST">
                    <input hidden name="s" value={sessionId} readOnly />
                  </form>
                  <ModalConfirmFreeReservation
                    open={openConfirmFreeModal}
                    onClose={cancelReservationWithGiftCard}
                    onReserve={doReserve}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="EntryConfirmationBlock">
        <LineTitleGray>取扱店舗</LineTitleGray>
        <div className="EntryConfirmationItem">
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">店舗名</div>
            <div className="userInfoItemDetail">{office && office.name}</div>
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">住所</div>
            <div className="userInfoItemDetail">
              {`〒${(office && office.postalCode) || ''} ${(office && office.address) || ''}`}
            </div>
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">電話番号</div>
            <div className="userInfoItemDetail">{office && office.tel}</div>
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">営業時間</div>
            <div
              className="userInfoItemDetail"
              dangerouslySetInnerHTML={{ __html: office && office.businessHours }}
            />
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">休業日</div>
            <div className="userInfoItemDetail">{office && office.holiday}</div>
          </div>
          {office && office.businessComment && (
            <div className="userInfoItem hasBorderLine">
              <div className="userInfoItemDetail">{office && office.businessComment}</div>
            </div>
          )}
          <div className="userInfoItem">
            <div className="userInfoItemName hasBorderLine">総合旅行業務取扱管理者</div>
            <div className="userInfoItemDetail">
              <div className="mainText">{office && office.managerName}</div>
            </div>
          </div>
          <div className="subText">
            旅行業務取扱管理者とは、お客様の旅行を取り扱う支店(営業所)での取引に関する責任者です。 この旅行契約に関し、担当者からの説明に不明な点があれば、ご遠慮なく取扱管理者にお尋ねください。
          </div>
        </div>
      </div>
      {isTimeoutError && <TimeoutErrorModal />}
    </div>
  );
};

FinalConfirmation.propTypes = {
  session: PropTypes.object,
};

export default FinalConfirmation;

const hasDiscount = (coupons) => !!coupons.find((c) => c && c.type === COUPON_TYPES.discount);

const filterValidCoupons = (coupons, totalPrice) => coupons.filter((c) => c.totalDiscount <= totalPrice);
